import React from 'react'
import { useDispatch } from 'react-redux'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { toast } from '@/components/toasts/toast.helpers'
import { reportPassphrase } from '@/reducers/organizations/organization.actions'
import { resetUserMFAConfig } from '@/reducers/auth/mfa.actions'

import { PasswordForm } from './forms/PasswordForm'
import { SupportActionsModal } from './SupportActionsModal'
import { TemporaryLinkForm } from './forms/TemporaryLinkForm'
import { UserAuditTable } from './UserAuditTable'
import { UserForm } from './UserForm'

export const UserTableModals = ({
  activeModal,
  activeRow,
  closeActiveModal,
  handleUserUpdate,
  generateTemporaryLink,
  generatePassphrase,
  playbooks,
  tags,
  tagCategories,
  qaEnabled,
  qaCopilotEnabled,
  agentGPTEnabled,
  orgRealtimeCoachingAccess,
  orgDesktopEnterpriseEnabled,
  flags,
  toolsEnabled,
}) => {
  const dispatch = useDispatch()

  if (!activeModal || !activeRow) {
    return null
  }

  const updateUserAccess = (user, accessType) => {
    const action = user[accessType] ? 'revoked' : 'granted'
    handleUserUpdate(
      { ...user, [accessType]: !user[accessType] },
      `Permission ${action} successfully`
    )
  }

  return (
    <>
      {activeModal === 'editUser' && (
        <BasicModal
          data-testid="edit-user-modal"
          title={`Edit ${activeRow.full_name}`}
          onClose={closeActiveModal}
          show
          size="small"
        >
          <UserForm
            onClose={closeActiveModal}
            modalClose={closeActiveModal}
            playbooks={playbooks}
            user={activeRow}
            tags={tags}
            tagCategories={tagCategories}
            qaEnabled={qaEnabled}
            qaCopilotEnabled={qaCopilotEnabled}
            agentGPTEnabled={agentGPTEnabled}
            realtimeCoachingAccessEnabled={orgRealtimeCoachingAccess}
            handleSubmitUser={(userData) => {
              handleUserUpdate(userData, 'User updated successfully')
              closeActiveModal()
            }}
            orgDesktopEnterpriseEnabled={orgDesktopEnterpriseEnabled}
            flags={flags}
            toolsEnabled={toolsEnabled}
          />
        </BasicModal>
      )}

      {activeModal === 'changeHistory' && (
        <BasicModal
          data-testid="change-history-modal"
          title={`Change History - ${activeRow.full_name}`}
          onClose={closeActiveModal}
          show
          size="large"
          scrolling
        >
          <UserAuditTable user={activeRow} />
        </BasicModal>
      )}

      {activeModal === 'resetPassword' && (
        <BasicModal
          data-testid="reset-password-modal"
          title={`Send Password Reset Email to ${activeRow.full_name}`}
          onClose={closeActiveModal}
          show
          size="small"
        >
          <TemporaryLinkForm
            onClose={closeActiveModal}
            updateUser={() => handleUserUpdate(activeRow.id)}
            user={activeRow}
            generateTemporaryLink={(expiration, user) => {
              const userCopy = { ...user }
              delete userCopy.user_tags
              generateTemporaryLink(expiration, userCopy)
            }}
          />
        </BasicModal>
      )}

      {activeModal === 'resetMfa' && (
        <BasicModal
          data-testid="reset-mfa-modal"
          title="Reset MFA Config"
          onClose={closeActiveModal}
          show
          size="tiny"
        >
          <ModalActions
            onClose={closeActiveModal}
            onSubmit={async () => {
              await dispatch(resetUserMFAConfig(activeRow.id))
              toast.success('MFA configuration reset successfully')
              window.location.reload()
            }}
            buttonLabel="Confirm"
          >
            <p>{`Are you sure you want to reset the multi-factor authentication configuration for ${activeRow.username}?`}</p>
          </ModalActions>
        </BasicModal>
      )}

      {activeModal === 'generatePassword' && (
        <BasicModal
          data-testid="generate-password-modal"
          title={`${activeRow.password ? 'Reset' : 'Add'} Password`}
          onClose={closeActiveModal}
          show
          size="small"
        >
          <PasswordForm
            onClose={closeActiveModal}
            modalClose={closeActiveModal}
            user={activeRow}
            updateUser={(user) => handleUserUpdate(user, 'Password updated successfully')}
            reportPassphrase={(badPassphrase) => reportPassphrase(badPassphrase)}
            generatePassphrase={(updatePassphrase) => generatePassphrase(updatePassphrase)}
          />
        </BasicModal>
      )}

      {activeModal === 'playbookManagement' && (
        <BasicModal
          data-testid="playbook-management-modal"
          title={`${activeRow.edit_config ? 'Revoke' : 'Grant'} Playbook Management`}
          onClose={closeActiveModal}
          show
          size="tiny"
        >
          <ModalActions
            onClose={closeActiveModal}
            onSubmit={() => updateUserAccess(activeRow, 'edit_config')}
            buttonLabel="Confirm"
          >
            <p>{`Are you sure you want to ${activeRow.edit_config ? 'revoke' : 'grant'} Playbook Editor access to ${activeRow.username}?`}</p>
          </ModalActions>
        </BasicModal>
      )}

      {activeModal === 'userManagement' && (
        <BasicModal
          data-testid="user-management-modal"
          title={`${activeRow.edit_users ? 'Revoke' : 'Grant'} User Management`}
          onClose={closeActiveModal}
          show
          size="tiny"
        >
          <ModalActions
            onClose={closeActiveModal}
            onSubmit={() => updateUserAccess(activeRow, 'edit_users')}
            buttonLabel="Confirm"
          >
            <p>{`Are you sure you want to ${activeRow.edit_users ? 'revoke' : 'grant'} user management access to ${activeRow.username}?`}</p>
          </ModalActions>
        </BasicModal>
      )}

      {activeModal === 'qaManagement' && (
        <BasicModal
          data-testid="qa-management-modal"
          title={`${activeRow.edit_qa ? 'Revoke' : 'Grant'} QA Access`}
          onClose={closeActiveModal}
          show
          size="tiny"
        >
          <ModalActions
            onClose={closeActiveModal}
            onSubmit={() => updateUserAccess(activeRow, 'edit_qa')}
            buttonLabel="Confirm"
          >
            <p>{`Are you sure you want to ${activeRow.edit_qa ? 'revoke' : 'grant'} QA access to ${activeRow.username}?`}</p>
          </ModalActions>
        </BasicModal>
      )}

      {activeModal === 'qaCopilotManagement' && (
        <BasicModal
          data-testid="qa-copilot-management-modal"
          title={`${activeRow.edit_qa_copilot ? 'Revoke' : 'Grant'} Quality Access`}
          onClose={closeActiveModal}
          show
          size="tiny"
        >
          <ModalActions
            onClose={closeActiveModal}
            onSubmit={() => updateUserAccess(activeRow, 'edit_qa_copilot')}
            buttonLabel="Confirm"
          >
            <p>{`Are you sure you want to ${activeRow.edit_qa_copilot ? 'revoke' : 'grant'} QA Copilot access to ${activeRow.username}?`}</p>
          </ModalActions>
        </BasicModal>
      )}

      {activeModal === 'supportActions' && (
        <SupportActionsModal users={[activeRow]} onClose={closeActiveModal} />
      )}
    </>
  )
}
